import { motion } from "framer-motion";
import {
  Radar, Wand2, Send, MessagesSquare, Target, Gauge, RefreshCw,
  Clapperboard, UserRound, Sparkles, ArrowRight, MessageCircle, Check, Film, Users, Crown,
} from "lucide-react";
import { WHATSAPP_LINK } from "@/lib/contact";

const ACCENT = "#6366F1";
const GLOW = "rgba(99,102,241,0.30)";
const DEMO_PREFILL = "Bonjour Pépé, je souhaite une démo de Giulia (GiulIATeck) pour mon entreprise.";

const POLES = [
  { n: "01", icon: Radar, title: "Veille", text: "Surveillance des concurrents (Instagram, TikTok, Facebook, LinkedIn), score viral (posts ×5 à ×20 la normale), palmarès du jour, veille des pubs concurrentes et des tendances." },
  { n: "02", icon: Wand2, title: "Création", text: "Brand kit, voix de la marque qui apprend, scripts seconde par seconde, contenu décliné sur tous les formats, vidéos UGC IA, influenceur/mascotte IA, pubs statiques, packshots et vidéos cinématiques." },
  { n: "03", icon: Send, title: "Diffusion", text: "Calendrier éditorial, validation client en 1 clic, publication programmée et test de pubs en volume." },
  { n: "04", icon: MessagesSquare, title: "Capture & réponse", text: "Réponse instantanée aux leads, appel manqué → SMS automatique, chatbot sur le site et agent vocal 24/7." },
  { n: "05", icon: Target, title: "Conversion", text: "Prise de rendez-vous avec rappels, relance des anciens clients et devis non signés, avis Google automatiques." },
  { n: "06", icon: Gauge, title: "Pilotage", text: "Tableau de bord, rapport mensuel + débrief humain, et un chat avec Giulia : « fais-moi 10 pubs pour Noël »." },
];

const PACKS = [
  { icon: Film, name: "Giulia Contenu", text: "Veille + scripts + calendrier + vidéos et visuels IA chaque mois." },
  { icon: Users, name: "Giulia Clients", text: "Capture + réponse 24/7 + prise de RDV + relances + réputation." },
  { icon: Crown, name: "Giulia Complète", text: "Les 6 pôles réunis + débrief mensuel avec Pépé.", flagship: true },
];

const ENGAGEMENTS = [
  "Rien n'est publié sans votre validation.",
  "Les contenus IA sont identifiés comme tels.",
  "Vos données sont isolées par client (RGPD).",
  "On s'engage sur des actions concrètes, pas sur des promesses.",
];

const reveal = {
  initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 }, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const DemoBadge = ({ label = "Démo / concept" }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[10px] uppercase tracking-wide font-bold" style={{ borderColor: `${ACCENT}66`, color: "#c7d2fe" }} data-testid="giuliateck-demo-badge">
    <Sparkles size={11} />{label}
  </span>
);

const ImagePlaceholder = ({ icon: Icon, caption, ratio = "aspect-[4/5]", testid }) => (
  <div className={`relative ${ratio} w-full rounded-2xl overflow-hidden grid place-items-center text-center`} data-testid={testid}
    style={{ border: `2px dashed ${ACCENT}55`, background: "linear-gradient(145deg, rgba(99,102,241,0.14), rgba(5,8,22,0.4))" }}>
    <div className="px-6">
      <Icon size={40} style={{ color: ACCENT }} className="mx-auto" />
      <p className="mt-3 font-mono-ui text-[11px] uppercase tracking-widest text-slate-400">{caption}</p>
      <p className="mt-1 text-[11px] text-slate-500">Tu fourniras l'image</p>
    </div>
  </div>
);

const DemoButton = ({ onQuote, service, prefill = DEMO_PREFILL, label = "Demander une démo", testid }) => (
  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={() => onQuote(service, prefill)} data-testid={testid}
    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold uppercase tracking-wide text-sm text-white" style={{ background: ACCENT, boxShadow: `0 0 26px ${GLOW}` }}>
    {label}<ArrowRight size={16} />
  </motion.button>
);

const WhatsAppButton = ({ testid }) => (
  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-bold uppercase tracking-wide text-sm border transition-colors hover:bg-white/5" style={{ borderColor: `${ACCENT}88`, color: "#c7d2fe" }}>
    <MessageCircle size={16} />WhatsApp
  </a>
);

const PoleCard = ({ pole, index }) => {
  const Icon = pole.icon;
  return (
    <motion.article {...reveal} transition={{ ...reveal.transition, delay: 0.05 * index }}
      className="glass border border-line rounded-2xl p-5 hover:border-slate-600 transition-colors" data-testid={`giuliateck-pole-${index}`}>
      <div className="flex items-center justify-between">
        <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: GLOW, color: "#c7d2fe" }}><Icon size={20} /></span>
        <span className="font-display text-3xl" style={{ color: `${ACCENT}99` }}>{pole.n}</span>
      </div>
      <h3 className="font-display uppercase text-xl mt-4" style={{ color: "#e0e7ff" }}>{pole.title}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed">{pole.text}</p>
    </motion.article>
  );
};

const PackCard = ({ pack, index, onQuote, service }) => {
  const Icon = pack.icon;
  const prefill = `Bonjour Pépé, je souhaite une démo du pack « ${pack.name} » de Giulia (GiulIATeck).`;
  return (
    <motion.article {...reveal} transition={{ ...reveal.transition, delay: 0.05 * index }}
      className={`relative rounded-2xl p-6 flex flex-col ${pack.flagship ? "bg-[rgba(99,102,241,0.08)]" : "glass"}`}
      style={{ border: `1px solid ${pack.flagship ? ACCENT : "var(--line, rgba(148,163,184,0.15))"}`, boxShadow: pack.flagship ? `0 0 40px ${GLOW}` : "none" }}
      data-testid={`giuliateck-pack-${index}`}>
      {pack.flagship && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide text-white" style={{ background: ACCENT }}>Pack phare</span>}
      <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: GLOW, color: "#c7d2fe" }}><Icon size={20} /></span>
      <h3 className="font-display uppercase text-2xl mt-4">{pack.name}</h3>
      <p className="text-sm text-slate-400 mt-2 leading-relaxed flex-1">{pack.text}</p>
      <div className="mt-5"><DemoButton onQuote={onQuote} service={service} prefill={prefill} testid={`giuliateck-pack-demo-${index}`} /></div>
    </motion.article>
  );
};

const GiuliaIntro = ({ onQuote, service }) => (
  <motion.div {...reveal} className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
    <div className="relative">
      <div aria-hidden="true" className="absolute -inset-2 rounded-3xl blur-2xl opacity-70" style={{ background: GLOW }} />
      <div className="relative"><ImagePlaceholder icon={UserRound} caption="Portrait de Giulia" testid="giuliateck-giulia-image" /></div>
    </div>
    <div>
      <h3 className="font-display uppercase text-3xl sm:text-4xl leading-none">Une collaboratrice qui ne dort jamais</h3>
      <p className="mt-5 text-slate-300 leading-relaxed">
        Giulia a le look d'une consultante : pro, posée, et assumée comme une IA. On la configure selon ton métier, puis elle surveille, crée, répond et relance pour toi — tu gardes la main, elle exécute.
      </p>
      <div className="mt-7 flex flex-wrap gap-3">
        <DemoButton onQuote={onQuote} service={service} testid="giuliateck-demo-button" />
        <WhatsAppButton testid="giuliateck-whatsapp-button" />
      </div>
    </div>
  </motion.div>
);

export const GiulIATeckSection = ({ service, onQuote }) => (
  <section id="giuliateck" className="relative py-20 sm:py-28 overflow-hidden" data-testid="giuliateck-section">
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(900px circle at 50% 0%, ${GLOW}, transparent 70%)` }} />
    <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
      <motion.div {...reveal} className="max-w-3xl">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: ACCENT }}>GiulIATeck — la marque IA de Pépé</p>
        <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">Voici <span className="text-outline">Giulia</span>, ton opérateur IA</h2>
        <blockquote className="mt-6 border-l-2 pl-5 text-slate-200 text-base sm:text-lg leading-relaxed" style={{ borderColor: ACCENT }} data-testid="giuliateck-accroche">
          « Giulia amène des clients à votre entreprise. Elle surveille votre marché, crée vos contenus et vos pubs, répond à vos prospects 24/7 et remplit votre agenda. <strong className="text-white">Vous validez, elle exécute.</strong> »
        </blockquote>
        <div className="mt-5"><DemoBadge label="Démos & concepts présentés comme tels" /></div>
      </motion.div>

      <GiuliaIntro onQuote={onQuote} service={service} />

      <div className="mt-16">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-2"><RefreshCw size={13} style={{ color: ACCENT }} />Un cycle continu, en 6 pôles</p>
        <p className="text-slate-400 text-sm mb-6">Veille → Création → Diffusion → Capture & réponse → Conversion → Pilotage, puis on recommence, en s'améliorant à chaque tour.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="giuliateck-poles">
          {POLES.map((pole, index) => <PoleCard key={pole.n} pole={pole} index={index} />)}
        </div>
      </div>

      <div className="mt-16">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-6">Trois façons de démarrer</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch" data-testid="giuliateck-packs">
          {PACKS.map((pack, index) => <PackCard key={pack.name} pack={pack} index={index} onQuote={onQuote} service={service} />)}
        </div>
        <p className="mt-4 text-sm text-slate-400" data-testid="giuliateck-pricing-note">Installation + abonnement mensuel, sur devis. <strong className="text-slate-200">Rien à la pièce.</strong></p>
      </div>

      <motion.div {...reveal} className="mt-14 glass border border-line rounded-2xl p-6 sm:p-8" data-testid="giuliateck-engagements">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.3em] mb-5" style={{ color: ACCENT }}>Nos engagements</p>
        <ul className="grid sm:grid-cols-2 gap-4">
          {ENGAGEMENTS.map((item) => (
            <li key={item} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
              <span className="shrink-0 grid place-items-center w-6 h-6 rounded-full mt-0.5" style={{ background: GLOW, color: "#c7d2fe" }}><Check size={14} /></span>{item}
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div {...reveal} className="mt-16 glass border border-line rounded-2xl p-6 sm:p-8 grid lg:grid-cols-2 gap-8 items-center" data-testid="giuliateck-pepe-block">
        <div className="lg:order-2 relative">
          <div aria-hidden="true" className="absolute -inset-2 rounded-3xl blur-2xl opacity-60" style={{ background: GLOW }} />
          <div className="relative"><ImagePlaceholder icon={Clapperboard} caption="Pepe, influenceur IA (vidéo)" ratio="aspect-[4/3]" testid="giuliateck-pepe-image" /></div>
        </div>
        <div className="lg:order-1">
          <DemoBadge />
          <h3 className="font-display uppercase text-2xl sm:text-3xl mt-4">Démo maison : Pepe, notre influenceur IA</h3>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Pour prouver qu'on sait créer un personnage IA de A à Z, on a donné vie à <strong className="text-slate-100">Pepe</strong> : moi, vieilli à 70 ans, Sicilien de Piazza Armerina. Entièrement créé en interne avec Higgsfield, il tourne sur TikTok et Instagram.
          </p>
          <p className="mt-3 text-sm text-slate-400">Un avatar, une voix, une personnalité — la même recette qu'on peut appliquer à ta marque.</p>
        </div>
      </motion.div>

      <motion.div {...reveal} className="mt-12 glass border border-line rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" style={{ boxShadow: `0 0 50px ${GLOW}` }}>
        <div>
          <h3 className="font-display uppercase text-2xl sm:text-3xl">Envie de voir Giulia en action ?</h3>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">Demande une démo — le premier échange est gratuit et sans engagement.</p>
        </div>
        <div className="shrink-0 flex flex-wrap gap-3">
          <DemoButton onQuote={onQuote} service={service} testid="giuliateck-final-demo-button" />
          <WhatsAppButton testid="giuliateck-final-whatsapp-button" />
        </div>
      </motion.div>
    </div>
  </section>
);
